import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ArrowUpDown, Plus } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LockedColumnRow } from "./LockedColumnRow";
import { SortableColumnRow } from "./SortableColumnRow";
import { i18n } from "@tellescope/i18n";
import type { SortingState } from "@tanstack/react-table";

export interface ColumnConfigFormProps {
  columnVisibility: Record<string, boolean>;
  orderedVisibleColumnIds: string[];
  combinedFieldToColumnConfigMap: Record<string, { label?: string }>;
  stagedColumnIds: string[];
  onStagedColumnIdsChange: (ids: string[]) => void;
  title?: string;
  stagedSorting?: SortingState;
  onStagedSortingChange?: Dispatch<SetStateAction<SortingState>>;
  isDirty: boolean;
  reset: () => void;
}

export const ColumnConfigForm = ({
  columnVisibility,
  orderedVisibleColumnIds: _orderedVisibleColumnIds,
  combinedFieldToColumnConfigMap,
  stagedColumnIds,
  onStagedColumnIdsChange,
  title = i18n.t("Columns"),
  stagedSorting,
  onStagedSortingChange,
  isDirty,
  reset,
}: ColumnConfigFormProps) => {
  const { t } = useTranslation();
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);

  const stagedDraggableIds = useMemo(() => stagedColumnIds.filter((id) => id !== "Actions"), [stagedColumnIds]);
  const stagedHasActions = useMemo(() => stagedColumnIds.includes("Actions"), [stagedColumnIds]);

  const allAvailableColumnIds = Object.keys(columnVisibility);

  const hiddenColumnOptions = useMemo(
    () =>
      allAvailableColumnIds
        .filter((id) => !stagedColumnIds.includes(id))
        .map((id) => ({
          value: id,
          label: combinedFieldToColumnConfigMap[id]?.label ?? id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allAvailableColumnIds, stagedColumnIds, combinedFieldToColumnConfigMap]
  );

  const getLabel = (id: string) => combinedFieldToColumnConfigMap[id]?.label ?? id;

  const getSortDirection = (id: string): "asc" | "desc" | false => {
    const sort = stagedSorting?.find((s) => s.id === id);
    if (!sort) return false;
    return sort.desc ? "desc" : "asc";
  };

  const handleToggleSort = useCallback(
    (id: string) => {
      if (!onStagedSortingChange) return;
      onStagedSortingChange((prev) => {
        const existing = prev.find((s) => s.id === id);
        if (!existing) return [{ id, desc: false }];
        if (!existing.desc) return [{ id, desc: true }];
        return prev.filter((s) => s.id !== id);
      });
    },
    [onStagedSortingChange]
  );

  const handleRemove = (id: string) => {
    onStagedColumnIdsChange(stagedColumnIds.filter((col) => col !== id));
  };

  const handleAdd = (id: string) => {
    const withoutActions = stagedColumnIds.filter((col) => col !== "Actions");
    const hasActions = stagedColumnIds.includes("Actions");
    onStagedColumnIdsChange([...withoutActions, id, ...(hasActions ? ["Actions"] : [])]);
    setAddPopoverOpen(false);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = stagedDraggableIds.indexOf(String(active.id));
    const newIndex = stagedDraggableIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(stagedDraggableIds, oldIndex, newIndex);
    onStagedColumnIdsChange([...reordered, ...(stagedHasActions ? ["Actions"] : [])]);
  };

  return (
    <div className="space-y-2 font-semibold">
      <div className="flex w-full justify-between items-center">
        <h3 className={cn("text-sm font-medium flex gap-2 items-center", isDirty && "text-green-600")}>
          <ArrowUpDown size={16} />
          {title}
        </h3>
        {isDirty && (
          <Button variant="link" className="underline text-xs text-muted-foreground p-0 h-2" onClick={reset}>
            {t("Reset")}
          </Button>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={stagedDraggableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {stagedDraggableIds.map((id) => (
              <SortableColumnRow
                key={id}
                id={id}
                label={getLabel(id)}
                onRemove={() => handleRemove(id)}
                sortDirection={onStagedSortingChange ? getSortDirection(id) : undefined}
                onToggleSort={onStagedSortingChange ? () => handleToggleSort(id) : undefined}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {stagedHasActions && (
        <div className="space-y-1">
          <LockedColumnRow label={getLabel("Actions")} />
        </div>
      )}

      {hiddenColumnOptions.length > 0 && (
        <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen} modal={false}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="mt-1 gap-1.5">
              <Plus className="h-4 w-4" />
              {t("Add Column")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start" onWheel={(e) => e.stopPropagation()}>
            <Command>
              <CommandInput placeholder={t("Search columns...")} />
              <CommandList>
                <CommandEmpty>{t("No columns found")}</CommandEmpty>
                <CommandGroup>
                  {hiddenColumnOptions.map((option) => (
                    <CommandItem key={option.value} value={option.label} onSelect={() => handleAdd(option.value)}>
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
